import { useState, useEffect } from 'react';
import { Shield, Users, Key, FileText, X, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';

const STORAGE_KEY_PREFIX = 'datarefinery_admin_banner_dismissed_';

export const AdminWelcomeBanner = ({ user, onNavigate }) => {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (!user || user.role !== 'admin' || !user.id) return;
    const dismissed = localStorage.getItem(`${STORAGE_KEY_PREFIX}${user.id}`);
    if (!dismissed) setVisible(true);
  }, [user]);

  const handleDismiss = () => {
    if (user?.id) {
      localStorage.setItem(`${STORAGE_KEY_PREFIX}${user.id}`, '1');
    }
    setVisible(false);
  };

  const handleGoTo = (tab) => {
    handleDismiss();
    if (onNavigate) onNavigate(tab);
  };

  if (!visible) return null;

  return (
    <div
      className="relative bg-gradient-to-r from-[#0F172A] via-[#1e293b] to-[#0F172A] text-white rounded-sm p-6 mb-6 shadow-lg overflow-hidden"
      data-testid="admin-welcome-banner"
    >
      <div className="absolute -right-10 -top-10 w-40 h-40 bg-blue-500/10 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute -left-10 -bottom-10 w-32 h-32 bg-purple-500/10 rounded-full blur-3xl pointer-events-none" />

      <button
        onClick={handleDismiss}
        className="absolute top-3 right-3 z-20 text-slate-400 hover:text-white transition-colors p-1"
        aria-label="Dismiss welcome banner"
        data-testid="admin-banner-dismiss"
      >
        <X size={18} />
      </button>

      <div className="relative flex items-start gap-4">
        <div className="w-11 h-11 bg-blue-500/20 rounded-sm flex items-center justify-center flex-shrink-0 border border-blue-400/30">
          <Shield size={22} className="text-blue-300" />
        </div>
        <div className="flex-1">
          <h2 className="text-lg font-heading font-semibold mb-1">
            Welcome, {user?.name || 'Admin'}
          </h2>
          <p className="text-sm text-slate-300 mb-4 max-w-2xl">
            You're signed in with administrator access. Here's where to set things up for your team:
          </p>
          <div className="flex flex-wrap gap-2">
            <Button
              size="sm"
              onClick={() => handleGoTo('admin')}
              className="bg-blue-500 hover:bg-blue-600 text-white gap-1.5 font-medium"
              data-testid="admin-banner-cta-users"
            >
              <Users size={14} /> Invite team & manage roles <ArrowRight size={14} />
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => handleGoTo('apikeys')}
              className="bg-transparent border-white/20 text-white hover:bg-white/10 gap-1.5 font-medium"
              data-testid="admin-banner-cta-apikeys"
            >
              <Key size={14} /> API keys
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => handleGoTo('audit')}
              className="bg-transparent border-white/20 text-white hover:bg-white/10 gap-1.5 font-medium"
              data-testid="admin-banner-cta-audit"
            >
              <FileText size={14} /> Audit log
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};
